import { contrastRatio } from '@escala/domain/palette';

/**
 * Primitivos de desenho da escala exportada (imagem e PDF). As cores são fixas: o
 * arquivo sai do aplicativo e não segue o tema — é a exceção a "hex solto" registrada
 * no DESIGN.md.
 */

export const COLORS = {
  page: '#F3F5FA',
  card: '#FFFFFF',
  cardLine: '#E3E8F0',
  shadow: 'rgba(15, 23, 42, 0.06)',
  ink: '#0F172A',
  body: '#334155',
  muted: '#64748B',
  faint: '#94A3B8',
  rule: '#E8ECF3',
  chip: '#F1F5F9',
  chipLine: '#E2E8F0',
  primary: '#2563EB',
  primaryDeep: '#1E40AF',
  primarySoft: '#EFF6FF',
  primaryLine: '#93C5FD',
  warn: '#B45309',
  warnSoft: '#FFFBEB',
  ok: '#15803D',
  okSoft: '#F0FDF4',
  white: '#FFFFFF',
} as const;

const STACK = "'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', sans-serif";

export const font = (weight: number, size: number) => `${weight} ${size}px ${STACK}`;

/** Pesos usados no desenho: carregados antes, senão o canvas cai na fonte do sistema. */
export const FONT_WEIGHTS = [500, 600, 700, 800] as const;

export type Ctx = CanvasRenderingContext2D;

/** Canvas `width × height` (unidades lógicas) com `scale` pixels por unidade. */
export function createCanvas(
  width: number,
  height: number,
  scale: number
): { canvas: HTMLCanvasElement; ctx: Ctx } {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');

  if (!ctx) throw new Error('Canvas indisponível');

  ctx.scale(scale, scale);
  ctx.textBaseline = 'alphabetic';

  return { canvas, ctx };
}

/** O navegador desenha em canvas? (O jsdom dos testes não.) */
export function canvasSupported(): boolean {
  try {
    return typeof document !== 'undefined' && !!document.createElement('canvas').getContext('2d');
  } catch {
    return false;
  }
}

export function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);

  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/** Corta o texto com reticências para caber em `max`. */
export function ellipsize(ctx: Ctx, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;

  let end = text.length;

  while (end > 0 && ctx.measureText(`${text.slice(0, end)}…`).width > max) end--;

  return `${text.slice(0, end).trimEnd()}…`;
}

/** Quebra em até `maxLines` linhas; a última leva reticências se sobrar texto. */
export function wrapLines(ctx: Ctx, text: string, max: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';

  for (const [i, word] of words.entries()) {
    const next = line ? `${line} ${word}` : word;

    if (ctx.measureText(next).width <= max || !line) {
      line = next;
      continue;
    }

    if (lines.length === maxLines - 1) {
      return [...lines, ellipsize(ctx, `${line} ${words.slice(i).join(' ')}`, max)];
    }

    lines.push(line);
    line = word;
  }

  return line ? [...lines, ellipsize(ctx, line, max)] : lines;
}

/** Mistura duas cores `#rrggbb` (t = 0 → a, t = 1 → b). */
export function headerColor(color: string): string {
  for (let step = 0; step <= 10; step++) {
    const candidate = mix(color, '#000000', step / 10);

    if (contrastRatio(candidate, COLORS.white) >= 4.5) return candidate;
  }

  return '#000000';
}

export function mix(a: string, b: string, t: number): string {
  const parse = (hex: string) => {
    const n = Number.parseInt(hex.replace('#', '').slice(0, 6), 16);

    return [(n >> 16) & 255, (n >> 8) & 255, n & 255] as const;
  };
  const [r1, g1, b1] = parse(a);
  const [r2, g2, b2] = parse(b);
  const ch = (x: number, y: number) =>
    Math.round(x + (y - x) * t)
      .toString(16)
      .padStart(2, '0');

  return `#${ch(r1, r2)}${ch(g1, g2)}${ch(b1, b2)}`;
}

/** Converte o canvas em arquivo. */
export function canvasBlob(
  canvas: HTMLCanvasElement,
  type: 'image/png' | 'image/jpeg',
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Falha ao gerar a imagem'))),
      type,
      quality
    )
  );
}
